import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, Button, Container, Typography, Box } from '@mui/material';

// Import React-Quill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { api } from '../api/api-client.config';
import {
  languages,
  visibleLanguages,
  quillFormats,
  quillModules,
} from '../config/quill-text-editor-settings';
import { LanguageTabs } from '../components/language-tabs';
import { TabPanel } from '../components/language-tabs/TabPanel';
import Iconify from '../components/iconify';
import { RoutesKeys } from 'src/enums/routesKeys.enum';
import { useAppSearchParams } from '../hooks/useAppSearchParams';
import { useGetPageQuery } from '../api/pages/getPage';
import { PageType } from 'src/types/domain.types';

// Initialize default content state
const defaultContent = {
  page_key: '',
  content_en: '',
  content_fr: '',
  content_es: '',
  content_lt: '',
  content_ru: '',
  content_de: '',
  content_sp: '',
  content_ca: '',
} as unknown as PageType;

export default function PageEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<PageType>(defaultContent);

  const [value, setValue] = useState(0);

  const { pageId } = useAppSearchParams();

  const { data: page } = useGetPageQuery({ pageId });

  useEffect(() => {
    if (!pageId) return;

    const newState: PageType = { ...defaultContent };
    for (let lang of languages) {
      const contentKey = `content_${lang}` as keyof PageType;
      (newState as any)[contentKey] = page?.[contentKey] || '';
    }

    setState(newState);
  }, [pageId, page]);

  const handleChangeTabs = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  const handleChange = (content: any, lang: any) => {
    setState((prevState) => ({
      ...prevState,
      [`content_${lang}`]: content,
    }));
  };

  const saveHandler = () => {
    const page: PageType = {
      page_id: pageId || 0,
      page_key: state.page_key || '',
      content_en: state.content_en || '',
      content_fr: state.content_fr || '',
      content_es: state.content_es || '',
      content_lt: state.content_lt || '',
      content_ru: state.content_ru || '',
      content_de: state.content_de || '',
      content_sp: state.content_sp || '',
      content_ca: state.content_ca || '',
    };

    setLoading(true);

    api
      .put(`/pages/${pageId}`, page)
      .then(() => {
        navigate(RoutesKeys.pages);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Helmet>
        <title> Page Edit </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Page Edit
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:save-outline' />}
            onClick={() => saveHandler()}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <LanguageTabs value={value} onChange={handleChangeTabs} />
        </Box>
        {visibleLanguages.map((lang, index) => (
          <TabPanel key={lang.code} value={value} index={index}>
            <Card sx={{ p: 2 }}>
              <ReactQuill
                value={state[
                  `content_${lang.code}` as keyof PageType
                ]?.toString()}
                onChange={(content) => handleChange(content, lang.code)}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '400px' }}
              />
            </Card>
          </TabPanel>
        ))}
      </Container>
    </>
  );
}
